import React, {useMemo, useState} from "react";
import {Alert, Button, Card, Grid, List, Modal, Pagination, Space, Table, Tag, Typography, message} from "antd";
import {ExclamationCircleFilled, UndoOutlined, ReloadOutlined} from "@ant-design/icons";
import {useDeletedChats, useRestoreChat} from "./deleted-items-query";

const {Text} = Typography;

/**
 * Conversations an administrator deleted, and the way back.
 *
 * <p>Exists because deleting is now reversible: without somewhere to see what was deleted, a soft
 * delete is just a disappearance nobody can undo, which is barely better than the hard delete it
 * replaced.
 */
const DeletedItems = () => {
    const screens = Grid.useBreakpoint();
    const isDesktop = !!screens.md;
    const [pagination, setPagination] = useState({current: 1, pageSize: 20});

    const {data, isLoading, isFetching, refetch} = useDeletedChats(pagination.current - 1, pagination.pageSize);
    const {mutate: restore, isPending: restoring} = useRestoreChat(() => message.success("گفت‌وگو بازگردانده شد"));

    const rows = useMemo(() => data?.content || data?.data?.content || [], [data]);
    const total = useMemo(() => data?.totalElements ?? data?.data?.totalElements ?? rows.length, [data, rows]);

    const confirmRestore = (row) => {
        Modal.confirm({
            title: "بازگرداندن گفت‌وگو",
            icon: <ExclamationCircleFilled style={{color: "#faad14"}}/>,
            content: (
                <div>
                    <p>این گفت‌وگو دوباره در صندوق اپراتور و تاریخچه نمایش داده می‌شود.</p>
                    {row.deleteReason && (
                        <Alert type="info" showIcon message={`دلیل حذف: ${row.deleteReason}`} style={{marginTop: 8}}/>
                    )}
                </div>
            ),
            okText: "بله، بازگردان",
            cancelText: "انصراف",
            onOk: () => restore(row.id),
        });
    };

    const when = (v) => (v ? new Date(v).toLocaleString("fa-IR") : "—");

    const columns = useMemo(() => [
        {
            title: "مخاطب",
            key: "who",
            render: (_, row) => (
                <Space direction="vertical" size={0}>
                    <Text>{row.fullName || row.user?.fullName || "—"}</Text>
                    <Tag color={String(row.channel).toUpperCase() === "WEB" ? "blue" : "cyan"}>
                        {String(row.channel).toUpperCase() === "WEB" ? "چت سایت" : "تلگرام"}
                    </Tag>
                </Space>
            ),
        },
        {title: "آخرین پیام", dataIndex: "lastMessage", ellipsis: true, render: (v) => v || "—"},
        {title: "زمان حذف", dataIndex: "deletedAt", width: 170, render: when},
        {title: "حذف توسط", dataIndex: "deletedByName", width: 150, render: (v) => v || "—"},
        {title: "دلیل", dataIndex: "deleteReason", width: 200, render: (v) => v || "—"},
        {
            title: "",
            key: "actions",
            width: 130,
            render: (_, row) => (
                <Button icon={<UndoOutlined/>} onClick={() => confirmRestore(row)} loading={restoring}>
                    بازگرداندن
                </Button>
            ),
        },
    ], [restoring]);

    return (
        <div style={{padding: 12}}>
            <Card
                title="موارد حذف‌شده"
                extra={
                    <Button icon={<ReloadOutlined/>} onClick={() => refetch()} loading={isFetching}>
                        به‌روزرسانی
                    </Button>
                }
            >
                <Alert
                    type="info"
                    showIcon
                    style={{marginBottom: 12}}
                    message="گفت‌وگوهای حذف‌شده پاک نمی‌شوند"
                    description="حذف فقط گفت‌وگو را از دید اپراتورها پنهان می‌کند. پیام‌ها و متن اصلی آن‌ها دست‌نخورده باقی می‌مانند و می‌توان گفت‌وگو را بازگرداند."
                />

                {isDesktop ? (
                    <Table
                        rowKey="id"
                        loading={isLoading}
                        dataSource={rows}
                        columns={columns}
                        pagination={false}
                        size="small"
                        scroll={{x: 1000}}
                        locale={{emptyText: "موردی حذف نشده است"}}
                    />
                ) : (
                    <List
                        loading={isLoading}
                        dataSource={rows}
                        locale={{emptyText: "موردی حذف نشده است"}}
                        renderItem={(row) => (
                            <List.Item
                                actions={[
                                    <Button key="r" size="small" icon={<UndoOutlined/>}
                                            onClick={() => confirmRestore(row)}>بازگرداندن</Button>,
                                ]}
                            >
                                <Space direction="vertical" size={2} style={{width: "100%"}}>
                                    <Text strong>{row.fullName || "—"}</Text>
                                    <Text type="secondary" style={{fontSize: 12}}>
                                        {when(row.deletedAt)} · {row.deletedByName || "—"}
                                    </Text>
                                    <Text ellipsis>{row.lastMessage || "—"}</Text>
                                </Space>
                            </List.Item>
                        )}
                    />
                )}

                {total > 0 && (
                    <div style={{display: "flex", justifyContent: "center", marginTop: 16}}>
                        <Pagination
                            current={pagination.current}
                            pageSize={pagination.pageSize}
                            total={total}
                            showSizeChanger
                            pageSizeOptions={["20", "50", "100"]}
                            onChange={(current, pageSize) => setPagination({current, pageSize})}
                        />
                    </div>
                )}
            </Card>
        </div>
    );
};

export default DeletedItems;
